import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { UiMessageBus } from './ui-message-bus';
import { PermissionsService } from '../permission/permissions.service';
import { RoleConstants } from '../../../common/roles.constrants';

/** Wire-shape of a UiMessage as sent by nucleus-ui-message-broker. */
export interface UiMessage {
    id: number;
    messageType: string;
    payload: string;
}

/**
 * Admin-only event types this UI subscribes to. Server-side, every backend
 * publishes UiMessage with one of these {@code messageType} strings; the
 * broker fans out to connected admin browsers and the bus dispatches to
 * the matching subject.
 *
 * Adding a new event type: add it here AND register it in the role-list
 * dispatch below — without the addEventListener call, the EventSource never
 * surfaces the named event in JS land and the message gets dropped.
 */
const ADMIN_EVENTS = [
    'RUNNER-UPDATED',
    'RUNNER-DELETED',
];

/**
 * Single shared SSE EventSource for the app. Opens on login, listens for
 * UiMessage events targeted at the current user (by userId or role),
 * dispatches into UiMessageBus.
 *
 * Mirrors bookwise-ui's UiMessageService — same SSE endpoint path,
 * same auth model (cookie-bearer translation in the BFF), same
 * NgZone re-entry on dispatch so subscribers' change detection fires.
 */
@Injectable({ providedIn: 'root' })
export class UiMessageService {

    private eventSource?: EventSource;

    constructor(
        private bus: UiMessageBus,
        private zone: NgZone,
        private http: HttpClient,
        private permissionsService: PermissionsService,
    ) {}

    connect(): void {
        if (this.eventSource) return;

        // BFF proxies this to https://10.0.0.184:9021/ui-message-broker/...
        // (see server/routes.js sse() entry). The broker's controller
        // authenticates via the cookie-stamped Authorization header.
        this.eventSource = new EventSource(
            '/api/ui-message-broker-service/ui/messages/stream',
            { withCredentials: true }
        );

        this.eventSource.onopen = () => this.bus.setConnected(true);

        this.eventSource.onerror = () => {
            this.bus.setConnected(false);
            // Try a token refresh; if that fails the session is dead, log out.
            this.http.post('/oauth2/refreshtoken',
                new URLSearchParams({ grant_type: 'refresh_token' }),
                { withCredentials: true })
                .subscribe({
                    next: () => this.reconnect(),
                    error: () => { this.disconnect(); window.location.assign('/oauth2/logout'); },
                });
        };

        this.eventSource.addEventListener('connected', () => {
            console.log('[SSE] connected');
            this.bus.setConnected(true);
        });
        this.eventSource.addEventListener('KEEP-ALIVE', () => { /* heartbeat — no-op */ });

        this.registerEventsByRole();
    }

    /**
     * Register addEventListener handlers for each event type the current
     * user is allowed to receive. EventSource only surfaces named events
     * that have a registered listener — drop a registration here and the
     * message silently disappears.
     */
    private registerEventsByRole(): void {
        const registered = new Set<string>();

        // NUC-86: every authenticated user listens for forced-logout pushes
        // so an admin-block / soft-delete kicks in for idle sessions too.
        this.register('FORCE_LOGOUT');
        registered.add('FORCE_LOGOUT');

        // TODO NUC-269: SSE topic fan-out is keyed on persona (which event streams
        // a user subscribes to), not on an access capability — an event type is not
        // a catalog entity:action. Left role-based; revisit if events gain a
        // capability taxonomy.
        if (this.permissionsService.hasAuthority(RoleConstants.ROLE_ADMIN)) {
            ADMIN_EVENTS.forEach(e => {
                if (!registered.has(e)) { this.register(e); registered.add(e); }
            });
        }
    }

    private register(type: string): void {
        this.eventSource?.addEventListener(type, (event) => this.handle(event as MessageEvent));
    }

    private handle(event: MessageEvent): void {
        const msg = JSON.parse(event.data);
        // Server sends payload as JSON-string. Double-parse so subscribers
        // get a typed object straight away.
        const payload = typeof msg.payload === 'string' ? JSON.parse(msg.payload) : msg.payload;

        if (msg.messageType === 'FORCE_LOGOUT') {
            this.zone.run(() => window.location.assign('/oauth2/logout'));
            return;
        }

        this.zone.run(() => this.bus.publish(msg.messageType, payload));
    }

    disconnect(): void {
        if (this.eventSource) {
            this.eventSource.close();
            this.eventSource = undefined;
        }
        this.bus.setConnected(false);
    }

    reconnect(): void {
        this.disconnect();
        this.connect();
    }
}
